import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, HelpCircle, Book, MessageSquare, Mail, ArrowRight, X } from 'lucide-react';
import Button from '../components/ui/Button';

const HelpCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    {
      title: 'Kom i gang',
      icon: <Book className="h-6 w-6" />,
      articles: [
        { title: 'Sådan opretter du en konto', link: '/help/create-account' },
        { title: 'Find det rigtige værktøj', link: '/help/find-tool' },
        { title: 'Foretag din første booking', link: '/help/first-booking' },
        { title: 'Forstå priser', link: '/help/understand-pricing' },
      ],
    },
    {
      title: 'Værktøjsleje',
      icon: <HelpCircle className="h-6 w-6" />,
      articles: [
        { title: 'Bookingprocessen forklaret', link: '/help/booking-process' },
        { title: 'Afhentning og returnering', link: '/help/pickup-return' },
        { title: 'Forlæng din leje', link: '/help/extend-rental' },
        { title: 'Afbestillingspolitik', link: '/help/cancellation-policy' },
      ],
    },
    {
      title: 'Betaling',
      icon: <MessageSquare className="h-6 w-6" />,
      articles: [
        { title: 'Sådan administrerer du betaling mellem parter', link: '/help/payment-management' },
        { title: 'Bedste praksis for sikker betaling', link: '/help/secure-payment' },
        { title: 'Hvad gør jeg ved betalingsproblemer?', link: '/help/payment-problems' },
        { title: 'Kontakt support ved tvister', link: '/help/dispute-support' },
      ],
    },
  ];

  const popularArticles = [
    {
      title: 'Hvordan fungerer forsikningsdækningen?',
      description: 'Lær om vores omfattende forsikringspolitik for værktøjsleje.',
      link: '/help/insurance-coverage',
    },
    {
      title: 'Hvad sker der hvis et værktøj går i stykker?',
      description: 'Forstå vores skadespolitik og løsningsproces.',
      link: '/help/tool-damage',
    },
    {
      title: 'Sådan kontakter du værktøjsejere',
      description: 'Tips til effektiv kommunikation med værktøjsejere.',
      link: '/help/contact-owners',
    },
  ];

  // Flatten alle artikler til søgning
  const allArticles = useMemo(() => {
    const articles: Array<{ title: string; link: string; category: string }> = [];
    
    categories.forEach((category) => {
      category.articles.forEach((article) => {
        articles.push({
          title: article.title,
          link: article.link,
          category: category.title,
        });
      });
    });
    
    popularArticles.forEach((article) => {
      articles.push({
        title: article.title,
        link: article.link,
        category: 'Populært',
      });
    });
    
    return articles;
  }, []);

  // Filtrer artikler baseret på søgning
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    return allArticles.filter((article) =>
      article.title.toLowerCase().includes(query) ||
      article.category.toLowerCase().includes(query)
    );
  }, [searchQuery, allArticles]);

  const isSearching = searchQuery.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Hvordan kan vi hjælpe?</h1>
            <p className="text-xl text-gray-600 mb-8">
              Søg i vores hjælpecenter eller gennemse kategorierne nedenfor
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Søg efter hjælpeartikler..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 text-gray-400"
                  aria-label="Ryd søgning"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {isSearching && (
        <div className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {searchResults.length > 0
                  ? `Fundet ${searchResults.length} resultat${searchResults.length !== 1 ? 'er' : ''}`
                  : 'Ingen resultater fundet'}
              </h2>
              
              {searchResults.length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
                  {searchResults.map((article) => (
                    <Link
                      key={article.link}
                      to={article.link}
                      className="block p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start">
                        <HelpCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-1">
                            {article.title}
                          </h3>
                          <p className="text-sm text-gray-500">{article.category}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">
                    Ingen artikler matcher din søgning "{searchQuery}"
                  </p>
                  <p className="text-sm text-gray-500">
                    Prøv at bruge andre søgeord eller gennemse kategorierne nedenfor
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Categories */}
      {!isSearching && (
        <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {categories.map((category) => (
              <div key={category.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-green-600 mb-4">{category.icon}</div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h2>
                <ul className="space-y-3">
                  {category.articles.map((article) => (
                    <li key={article.title}>
                      <Link
                        to={article.link}
                        className="text-gray-600 hover:text-green-600 flex items-center"
                      >
                        <ArrowRight className="h-4 w-4 mr-2" />
                        <span>{article.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Popular Articles */}
      {!isSearching && (
        <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Populære artikler</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {popularArticles.map((article) => (
              <Link
                key={article.title}
                to={article.link}
                className="block bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors duration-200"
              >
                <h3 className="text-lg font-medium text-gray-900 mb-2">{article.title}</h3>
                <p className="text-gray-600">{article.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Contact Support */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-600 rounded-lg shadow-lg text-white p-8">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4">Har du stadig brug for hjælp?</h2>
              <p className="text-green-100 mb-8">
                Vores supportteam er klar til at hjælpe dig med eventuelle spørgsmål eller bekymringer
              </p>
              <div className="max-w-md mx-auto">
                <div className="text-center">
                  <Mail className="h-8 w-8 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Email Support</h3>
                  <p className="text-green-100 mb-4">Få svar inden for 24 timer</p>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="!bg-white !text-green-600 !border-white hover:!bg-green-50"
                    >
                      Send Email
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;